import { useEffect, useState } from "react";
import type { Partner } from "../types";
import { useData } from "../hooks/useData";
import { api, OfflineError } from "../api/client";
import { clsx, fmtDate } from "../components/Primitives";

/** The one screen to open in the morning: what needs you, then today's tasks, then the rest at a glance. */

interface T {
  id: string; task: string; external_code: string | null; owner: string; status: string; due: string | null; priority: string;
  deal_name: string | null; waiting_on: string | null; reply_from?: string; reply_subject?: string; delegated_by_name?: string | null; draft_ready?: boolean;
}
interface TodayData {
  replies: T[]; handoffs: T[]; today: T[]; chase: T[]; emails_to_review: number; proposed_dates: number;
  radar: { id: string; title: string; kind: string }[]; newsletters: { id: string; name: string; subject: string }[]; needs_you: number;
}

const clean = (t: T) => (t.external_code && t.task.startsWith(t.external_code) ? t.task.slice(t.external_code.length).replace(/^\s*[:–-]\s*/, "") : t.task);
const T0 = () => new Date().toLocaleDateString("sv-SE");

export default function Today({ go, partners, myId, onChanged }: {
  go: (s: string, id?: string | null) => void; partners: Record<string, Partner>; myId: string; onChanged: () => void;
}) {
  const { data, refetch, error } = useData<TodayData>("/api/phone/today");
  // Website visitors this week, once Google Analytics is connected.
  const { data: webSt } = useData<{ analytics: boolean }>("/api/web/status");
  const { data: web } = useData<{ sites: Record<"www" | "info", { users: number; users_change: number | null }> }>(webSt?.analytics ? "/api/web/phone" : null);
  const [done, setDone] = useState<Record<string, boolean>>({});
  const [msg, setMsg] = useState<string | null>(null);
  useEffect(() => {
    // The app icon shows the same "needs you" number.
    const nav = navigator as Navigator & { setAppBadge?: (n: number) => Promise<void>; clearAppBadge?: () => Promise<void> };
    if (data && nav.setAppBadge) (data.needs_you ? nav.setAppBadge(data.needs_you) : nav.clearAppBadge?.())?.catch(() => {});
  }, [data]);
  const act = async (fn: () => Promise<unknown>, id: string, text: string) => {
    setDone(d => ({ ...d, [id]: true })); setMsg(null);
    try { await fn(); setMsg(text); refetch(); onChanged(); }
    catch (e) {
      // Offline: the change is queued on the phone, so it stays done here.
      if (!(e instanceof OfflineError)) setDone(d => ({ ...d, [id]: false }));
      setMsg(e instanceof Error ? e.message : String(e));
    }
  };
  const first = partners[myId]?.name.split(" ")[0] || "";
  const hour = new Date().getHours();
  const hello = hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";

  if (error && !data) return <div className="ph-pad"><div className="ph-note err">{error}</div></div>;
  if (!data) return <div className="ph-pad"><div className="ph-skel" /><div className="ph-skel" /><div className="ph-skel short" /></div>;
  const needs = data.replies.length + data.handoffs.length;
  const overdue = data.today.filter(t => t.due && t.due < T0()).length;

  return (
    <div className="ph-pad ph-today">
      <section className="ph-hero">
        <div className="ph-hero-h">{hello}{first ? `, ${first}` : ""}</div>
        <div className="ph-hero-s">{data.needs_you ? `${data.needs_you} thing${data.needs_you === 1 ? "" : "s"} need${data.needs_you === 1 ? "s" : ""} you today.` : "Nothing urgent — a good day to get ahead."}</div>
        <div className="ph-hero-k">
          <button onClick={() => go("tasks")}><b>{data.replies.length}</b>replies in</button>
          <button onClick={() => go("tasks")}><b>{data.handoffs.length}</b>handed to you</button>
          <button onClick={() => go("tasks")}><b className={overdue ? "warn" : ""}>{data.today.length}</b>due{overdue ? ` · ${overdue} late` : " today"}</button>
          <button onClick={() => go("inbox")}><b>{data.emails_to_review}</b>emails</button>
        </div>
      </section>
      {msg && <div className="ph-note">{msg}</div>}

      {needs > 0 && <>
        <h2 className="ph-h2">Needs you</h2>
        <div className="ph-card">
          {data.replies.filter(t => !done[t.id]).map(t => (
            <div key={t.id} className="ph-row">
              <span className="ph-av green">✉</span>
              <button className="ph-row-m" onClick={() => go("tasks", t.id)}>
                <span className="ph-row-t">Reply from {t.reply_from}</span>
                <span className="ph-row-s">{t.reply_subject} — {clean(t)}</span>
              </button>
              <button className="ph-chip ok" onClick={() => act(() => api.post(`/api/todos/${t.id}/reply`, { action: "done" }), t.id, "Done — the reply is the evidence")}>Done</button>
              <button className="ph-chip" aria-label="Not the answer" onClick={() => act(() => api.post(`/api/todos/${t.id}/reply`, { action: "dismiss" }), t.id, "Still waiting — that wasn't the answer")}>Not it</button>
            </div>
          ))}
          {data.handoffs.filter(t => !done[t.id]).map(t => (
            <div key={t.id} className="ph-row">
              <span className="ph-av blue">↪</span>
              <button className="ph-row-m" onClick={() => go("tasks", t.id)}>
                <span className="ph-row-t">{t.delegated_by_name || "A colleague"} handed you a task</span>
                <span className="ph-row-s">{clean(t)}</span>
              </button>
              <button className="ph-chip" onClick={() => act(() => api.post(`/api/todos/${t.id}/handoff`, { accept: true }), t.id, "On your list")}>Accept</button>
            </div>
          ))}
        </div>
      </>}

      <h2 className="ph-h2">Today {data.today.length > 5 && <button className="ph-link" onClick={() => go("tasks")}>All {data.today.length} ›</button>}</h2>
      {data.today.length === 0 && data.chase.length === 0 ? (
        <div className="ph-card ph-empty">Nothing due today.</div>
      ) : (
        <div className="ph-card">
          {[...data.chase.map(t => ({ ...t, chase: true })), ...data.today].slice(0, 6).map(t => (
            <div key={t.id} className={clsx("ph-row", done[t.id] && "ph-gone")}>
              <button className={clsx("ph-check", done[t.id] && "on")} aria-label="Complete"
                onClick={() => act(() => api.put(`/api/todos/${t.id}`, { status: "done" }), t.id, `Done: ${clean(t).slice(0, 50)}`)} />
              <button className="ph-row-m" onClick={() => go("tasks", t.id)}>
                <span className="ph-row-t">{"chase" in t ? `Chase ${t.waiting_on}: ` : ""}{clean(t)}</span>
                <span className="ph-row-s">{[t.external_code, t.deal_name, t.draft_ready ? "✦ Draft ready" : ""].filter(Boolean).join(" · ") || "—"}</span>
              </button>
              <span className={clsx("ph-due", t.due && t.due < T0() && "late")}>{t.due === T0() ? "Today" : t.due ? fmtDate(t.due) : ""}</span>
            </div>
          ))}
        </div>
      )}

      {(data.proposed_dates > 0 || data.newsletters.length > 0) && <>
        <h2 className="ph-h2">To decide</h2>
        <div className="ph-card">
          {data.newsletters.map(n => (
            <button key={n.id} className="ph-row ph-row-btn" onClick={() => go("newsletters", n.id)}>
              <span className="ph-av ember">✉</span>
              <span className="ph-row-m"><span className="ph-row-t">Approve: {n.name}</span><span className="ph-row-s">{n.subject}</span></span>
              <span className="ph-due">›</span>
            </button>
          ))}
          {data.proposed_dates > 0 && (
            <button className="ph-row ph-row-btn" onClick={() => go("tasks")}>
              <span className="ph-av">◷</span>
              <span className="ph-row-m"><span className="ph-row-t">{data.proposed_dates} dates proposed by AI or a bot</span><span className="ph-row-s">Agree them together in Tasks</span></span>
              <span className="ph-due">›</span>
            </button>
          )}
        </div>
      </>}

      {web && <>
        <h2 className="ph-h2">Web this week <button className="ph-link" onClick={() => go("web")}>Open ›</button></h2>
        <button className="ph-card ph-row ph-row-btn" onClick={() => go("web")}>
          {(["www", "info"] as const).map(s => (
            <span key={s} className="ph-row-m">
              <span className="ph-row-t">{web.sites[s].users.toLocaleString("en-GB")} <small className="ph-row-s">visitors</small></span>
              <span className="ph-row-s">{s}{web.sites[s].users_change !== null ? ` · ${web.sites[s].users_change! > 0 ? "+" : ""}${web.sites[s].users_change}%` : ""}</span>
            </span>
          ))}
        </button>
      </>}

      {data.radar.length > 0 && <>
        <h2 className="ph-h2">On the Radar <button className="ph-link" onClick={() => go("radar")}>Open ›</button></h2>
        <div className="ph-card">
          {data.radar.map(r => (
            <button key={r.id} className="ph-row ph-row-btn" onClick={() => go("radar")}>
              <span className="ph-row-m"><span className="ph-row-t ph-wrap">{r.title}</span><span className="ph-row-s">{r.kind}</span></span>
            </button>
          ))}
        </div>
      </>}
    </div>
  );
}
